import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Trash, Pencil, Download, Upload } from '@phosphor-icons/react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Country, State, City } from 'country-state-city';
import { useAuth } from '../contexts/AuthContext';
import { usePagination } from '../components/TablePagination';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const empty = () => ({
  id: '', first_name: '', last_name: '', email: '', contact_no: '',
  website: '', postal_code: '', country: 'IN', state: '', city: '',
  address: '', notes: '', company_name: '', gstin: '', logo: '',
});

const allCountries = Country.getAllCountries();

export const ClientsPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState(empty());
  const [search, setSearch] = useState('');
  const [gstFilter, setGstFilter] = useState('all'); // all | gst | nongst

  // Resolve country code from name if legacy data stored full name
  const countryCode = useMemo(() => {
    if (!form.country) return '';
    if (form.country.length === 2) return form.country;
    const c = allCountries.find((x) => x.name.toLowerCase() === form.country.toLowerCase());
    return c ? c.isoCode : '';
  }, [form.country]);

  const states = useMemo(() => (countryCode ? State.getStatesOfCountry(countryCode) : []), [countryCode]);

  const stateCode = useMemo(() => {
    if (!form.state || !countryCode) return '';
    if (form.state.length <= 3) {
      const exact = states.find((x) => x.isoCode === form.state);
      if (exact) return form.state;
    }
    const m = states.find((x) => x.name.toLowerCase() === form.state.toLowerCase());
    return m ? m.isoCode : '';
  }, [form.state, states, countryCode]);

  const cities = useMemo(() => (countryCode && stateCode ? City.getCitiesOfState(countryCode, stateCode) : []), [countryCode, stateCode]);

  const load = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/clients`, { withCredentials: true });
      setList(r.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (edit) await axios.put(`${API_URL}/api/clients/${form.id}`, form, { withCredentials: true });
      else await axios.post(`${API_URL}/api/clients`, form, { withCredentials: true });
      setOpen(false); setForm(empty()); setEdit(false); load();
    } catch (err) { alert('Save failed: ' + (err.response?.data?.detail || err.message)); }
  };

  const onEdit = (c) => { setForm({ ...empty(), ...c }); setEdit(true); setOpen(true); };
  const onDel = async (id) => {
    if (!window.confirm('Delete this client?')) return;
    await axios.delete(`${API_URL}/api/clients/${id}`, { withCredentials: true });
    load();
  };

  const downloadTemplate = () => {
    const header = 'first_name,last_name,company_name,email,contact_no,website,country,state,city,postal_code,address,gstin,notes';
    const sample = 'Acme,Corp,Acme Pvt Ltd,info@acme.com,9876543210,https://acme.com,IN,Tamil Nadu,Chennai,600001,No.1 Anna Salai,33AABCA1234A1Z5,VIP client';
    const csv = `${header}\n${sample}\n`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'clients_import_template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const importCsv = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API_URL}/api/clients/bulk-import`, fd, { withCredentials: true });
      alert(`${r.data.message}${r.data.errors?.length ? '\n\nFirst errors:\n' + r.data.errors.join('\n') : ''}`);
      load();
    } catch (e) { alert('Import failed: ' + (e.response?.data?.detail || e.message)); }
  };

  const filteredClients = useMemo(() => {
    const q = search.trim().toLowerCase();
    return list.filter((c) => {
      const hasGst = !!(c.gstin || '').trim();
      if (gstFilter === 'gst' && !hasGst) return false;
      if (gstFilter === 'nongst' && hasGst) return false;
      if (!q) return true;
      const blob = `${c.first_name || ''} ${c.last_name || ''} ${c.company_name || ''} ${c.email || ''} ${c.contact_no || ''} ${c.gstin || ''}`.toLowerCase();
      return blob.includes(q);
    });
  }, [list, search, gstFilter]);

  const { paged: pagedClients, controls, startIdx } = usePagination(filteredClients, { storageKey: 'clients-page-size', testIdPrefix: 'client' });

  return (
    <div data-testid="clients-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ color: 'var(--dx-text)' }}>Clients</h1>
          <p className="text-sm mt-1" style={{ color: 'var(--dx-text2)' }}>Manage your invoicing clients</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(empty()); setEdit(false); } }}>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="client-download-template-btn" title="Download CSV template">
              <Download size={14} className="mr-1.5" /> Template
            </Button>
            <label className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border rounded-md cursor-pointer hover:bg-gray-50"
              style={{ borderColor: 'var(--dx-border2)', height: '32px' }} data-testid="client-import-csv-label">
              <Upload size={14} /> Import CSV
              <input type="file" accept=".csv" className="hidden" data-testid="client-import-csv-input"
                onChange={(e) => { importCsv(e.target.files?.[0]); e.target.value = ''; }} />
            </label>
            <DialogTrigger asChild>
              <Button data-testid="add-client-btn" className="text-white" style={{ background: 'var(--dx-primary)' }}>
                <Plus size={18} className="mr-1" /> Add Client
              </Button>
            </DialogTrigger>
          </div>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{edit ? 'Edit Client' : 'Add New Client'}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>First Name *</Label><Input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="client-fname" /></div>
                <div><Label>Last Name</Label><Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="client-lname" /></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="client-email" /></div>
                <div><Label>Contact No.</Label><Input value={form.contact_no} onChange={(e) => setForm({ ...form, contact_no: e.target.value })} data-testid="client-contact" /></div>
                <div><Label>Company Name</Label><Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="client-company" /></div>
                <div><Label>GSTIN</Label><Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} data-testid="client-gstin" /></div>
                <div><Label>Website</Label><Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} /></div>
                <div><Label>Postal Code</Label><Input value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} /></div>
                <div>
                  <Label>Country</Label>
                  <select
                    className="w-full border rounded-md px-3 py-2 text-sm bg-white"
                    value={countryCode}
                    onChange={(e) => setForm({ ...form, country: e.target.value, state: '', city: '' })}
                    data-testid="client-country"
                  >
                    <option value="">— Select Country —</option>
                    {allCountries.map((c) => (
                      <option key={c.isoCode} value={c.isoCode}>{c.flag} {c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>State</Label>
                  <select
                    className="w-full border rounded-md px-3 py-2 text-sm bg-white disabled:bg-gray-50 disabled:text-gray-400"
                    value={stateCode}
                    onChange={(e) => setForm({ ...form, state: e.target.value, city: '' })}
                    disabled={!countryCode || states.length === 0}
                    data-testid="client-state"
                  >
                    <option value="">{countryCode ? (states.length ? '— Select State —' : 'No states') : 'Select country first'}</option>
                    {states.map((s) => (
                      <option key={s.isoCode} value={s.isoCode}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>City</Label>
                  <select
                    className="w-full border rounded-md px-3 py-2 text-sm bg-white disabled:bg-gray-50 disabled:text-gray-400"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    disabled={!stateCode || cities.length === 0}
                    data-testid="client-city"
                  >
                    <option value="">{stateCode ? (cities.length ? '— Select City —' : 'No cities') : 'Select state first'}</option>
                    {cities.map((ct) => (
                      <option key={`${ct.name}-${ct.latitude}-${ct.longitude}`} value={ct.name}>{ct.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div><Label>Address</Label>
                <textarea className="w-full border rounded-md px-3 py-2 text-sm min-h-[60px]" value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="client-address" />
              </div>
              <div><Label>Notes</Label>
                <textarea className="w-full border rounded-md px-3 py-2 text-sm min-h-[60px]" value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
              <div>
                <Label>Client Logo</Label>
                <div className="flex items-center gap-3 mt-1">
                  {form.logo && (
                    <img src={form.logo} alt="Client logo" className="w-14 h-14 object-contain border rounded-md bg-white p-1" />
                  )}
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      data-testid="client-logo-input"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 2 * 1024 * 1024) { alert('Logo must be under 2 MB'); return; }
                        const reader = new FileReader();
                        reader.onload = (ev) => setForm({ ...form, logo: ev.target.result });
                        reader.readAsDataURL(file);
                      }}
                      className="text-xs"
                    />
                    <p className="text-[10px] text-gray-500 mt-1">Will appear next to Dextra logo on the proposal cover.</p>
                  </div>
                  {form.logo && (
                    <Button type="button" variant="outline" size="sm" onClick={() => setForm({ ...form, logo: '' })}>Remove</Button>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" className="text-white" style={{ background: 'var(--dx-primary)' }} data-testid="save-client-btn">
                  {edit ? 'Update' : 'Save'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="dx-card">
        <div className="p-3 flex items-center gap-3 flex-wrap border-b" style={{ borderColor: 'var(--dx-border)' }}>
          <div className="relative flex-1 min-w-[240px]">
            <input
              type="text"
              placeholder="Search by name, company, email, GSTIN, contact…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full text-sm pl-3 pr-3 py-2 border rounded outline-none focus:ring-2 focus:ring-emerald-500/30"
              data-testid="client-search"
            />
          </div>
          <div className="flex items-center gap-1 text-xs">
            {[
              { k: 'all', label: 'All' },
              { k: 'gst', label: 'GST Clients' },
              { k: 'nongst', label: 'Non-GST' },
            ].map((f) => (
              <button
                key={f.k}
                onClick={() => setGstFilter(f.k)}
                className={`px-3 py-1.5 rounded-md border ${gstFilter === f.k ? 'bg-emerald-50 border-emerald-300 text-emerald-700 font-medium' : 'bg-white border-gray-200 text-gray-600'}`}
                data-testid={`client-filter-${f.k}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow style={{ background: 'var(--dx-bg)' }}>
              <TableHead>#</TableHead>
              <TableHead>NAME</TableHead><TableHead>COMPANY</TableHead><TableHead>EMAIL</TableHead>
              <TableHead>CONTACT</TableHead><TableHead>GSTIN</TableHead>
              {isAdmin && <TableHead className="text-center">INVOICES</TableHead>}
              <TableHead className="text-right">ACTIONS</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pagedClients.length === 0 ? (
              <TableRow><TableCell colSpan={isAdmin ? 8 : 7} className="text-center py-8" style={{ color: 'var(--dx-text2)' }}>No clients match the filter.</TableCell></TableRow>
            ) : pagedClients.map((c, i) => (
              <TableRow key={c.id} data-testid={`client-row-${c.id}`}>
                <TableCell className="text-gray-400">{startIdx + i + 1}</TableCell>
                <TableCell className="font-medium">{c.first_name} {c.last_name}</TableCell>
                <TableCell>{c.company_name || '-'}</TableCell>
                <TableCell style={{ color: 'var(--dx-text2)' }}>{c.email || '-'}</TableCell>
                <TableCell>{c.contact_no || '-'}</TableCell>
                <TableCell>{c.gstin ? <span className="font-mono text-xs">{c.gstin}</span> : <span className="text-gray-300">-</span>}</TableCell>
                {isAdmin && (
                  <TableCell className="text-center">
                    <div
                      className="inline-flex items-center gap-1.5"
                      title={`Total invoices: ${c.invoice_count || 0} · Paid: ${c.paid_invoice_count || 0} · Unpaid: ${c.unpaid_invoice_count || 0}`}
                    >
                      <span
                        className="inline-flex items-center justify-center min-w-[32px] h-6 px-2 rounded-full text-[11px] font-bold bg-slate-100 text-slate-700"
                        data-testid={`client-inv-total-${c.id}`}
                      >
                        {c.invoice_count || 0}
                      </span>
                      {(c.paid_invoice_count || 0) > 0 && (
                        <span className="text-[10px] font-semibold text-emerald-700" data-testid={`client-inv-paid-${c.id}`}>
                          ✓ {c.paid_invoice_count}
                        </span>
                      )}
                      {(c.unpaid_invoice_count || 0) > 0 && (
                        <span
                          className="text-[10px] font-semibold text-rose-700"
                          title={`₹${(c.outstanding_amount || 0).toLocaleString('en-IN')}`}
                          data-testid={`client-inv-unpaid-${c.id}`}
                        >
                          • {c.unpaid_invoice_count} due
                        </span>
                      )}
                    </div>
                  </TableCell>
                )}
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="outline" onClick={() => onEdit(c)} data-testid={`edit-client-${c.id}`}><Pencil size={14} /></Button>
                    <Button size="sm" variant="outline" className="text-red-600" onClick={() => onDel(c.id)} data-testid={`delete-client-${c.id}`}><Trash size={14} /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {controls}
      </Card>
    </div>
  );
};

export default ClientsPage;
